import React, { useState, useCallback, useEffect, Suspense, lazy, useMemo, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useEditor, EditorContent as TipTapEditorContent, BubbleMenu } from '@tiptap/react';
import type { Editor as TiptapEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold as BoldIcon, Italic as ItalicIcon, X } from 'lucide-react';
import Stars from '@/shared/icons/stars';
import MarkdownIt from 'markdown-it';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';
import AskAi from '@/modules/assistant/components';
import { useParams } from 'react-router-dom';
import { getAgentData, getAgentStateActions, useAgentStepData } from '@/modules/agent/states';
import InlineSources from '../review-responses/InlineSources';
import { ReviewSourceTypes, PDFReviewSource, ExcelReviewSource, AgentSubTypes } from '../../types/index.ts';
import { sourcePreviewSupportedFileExtensions } from '../../constants/sourcePreviewSupportedFileTypes';
import { checkAndRefreshUrl } from '../../utils/refreshUrl.ts';
import { addNotification } from '@/shared/states/notification';
import { Spinner } from '@nextui-org/react';
import useMemoAsync from '@/shared/hooks/useMemoAsync.ts';
import { handleAgentAutoSave } from '../../utils/autosave';
import { AGENT_TYPES, PolicyManagementTypes } from '../../types/index.ts';
import { AgentSessionStepType, AgentSessionStatus } from '@/modules/sessions/types';
import debounce from 'lodash/debounce';
import RefineTextModal from './RefineTextModal.tsx';
import { emitRagCreate } from '../../requests';
import { getResponseTypes } from '../../utils/get-response-type';
import { userStateSelector } from '@/shared/states/user';
import { AiResponseType } from '@/shared/types/user';
import { handleSocketResponse } from '../../utils/socket-response';
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableHeader from '@tiptap/extension-table-header'
import TableCell from '@tiptap/extension-table-cell'

// Lazy load preview components
const PDFViewer = lazy(() => import("@/shared/components/custom/pdf-preview"));
const JSONPreviewHighlight = lazy(() => import("../json-preview"));
const ExcelPreview = lazy(() => import("../excel-preview"));
const ImageViewer = lazy(
  () => import("@/shared/components/custom/image-viewer"),
);

// Initialize markdown-it instance
const md = new MarkdownIt({
  html: true,
  breaks: true,
  linkify: true,
  typographer: true
});

// Initialize turndown service for HTML to Markdown conversion
const turndownService = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
  fence: '```',
  emDelimiter: '*',
  strongDelimiter: '**',
  linkStyle: 'inlined',
}).use(gfm);

// Helper function to get file extension
const getFileExtension = (fileName: string | undefined): string | null => {
  if (!fileName) return null;
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1 || lastDotIndex === fileName.length - 1) return null;
  return fileName.substring(lastDotIndex + 1).toLowerCase();
};

// Convert markdown to HTML using markdown-it
const convertMarkdownToHTML = (markdown: string): string => {
  if (!markdown) return '';
  return md.render(markdown);
};

// Convert HTML to Markdown using turndown
const convertHTMLToMarkdown = (html: string): string => {
  if (!html) return '';
  return turndownService.turndown(html);
};


interface EditorContentProps {
  // Removed onAutoSaveStateChange since we're using state-based approach
}

const EditorContent = ({isRefine}:{isRefine:boolean}) => {
  console.log("isRefine", isRefine);
  const { id } = useParams<{ id: string }>();
  const [savedContent, setSavedContent] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [isFloatingAskAiOpen, setIsFloatingAskAiOpen] = useState(false);
  const [isRefineModalOpen, setIsRefineModalOpen] = useState(false);
  const [selectedTextForRefine, setSelectedTextForRefine] = useState('');
  const [selectedTextIndices, setSelectedTextIndices] = useState<{ start: number; end: number }>();
  const [refineModalPosition, setRefineModalPosition] = useState<{ top: number; left: number }>();
  const [isRefineSubmitted, setIsRefineSubmitted] = useState(false);
  const [selectedSource, setSelectedSource] = useState<ReviewSourceTypes>();
  const [listOfSourcesForSelectedSources, setListOfSourcesForSelectedSources] = useState<ReviewSourceTypes[]>();
  const [sources, setSources] = useState<ReviewSourceTypes[]>([]);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isTypographyMenuOpen, setIsTypographyMenuOpen] = useState(false);
  const [isAnimatingUpdate, setIsAnimatingUpdate] = useState(false);
  // Hack to preserve visual selection
  const [preservedSelection, setPreservedSelection] = useState<{ from: number; to: number; text: string } | null>(null);
  const [selectionOverlay, setSelectionOverlay] = useState<{ rects: DOMRect[]; text: string; color: string } | null>(null);
  const [browserSelectionColor, setBrowserSelectionColor] = useState<string>('rgba(0, 123, 255, 0.25)');
  const [animationLines, setAnimationLines] = useState<string[]>([]);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [lastProcessedContent, setLastProcessedContent] = useState<string>('');
  const [isAwaitingRefine, setIsAwaitingRefine] = useState(false);
  const isAwaitingRefineRef = useRef(false);
  const animationControls = useAnimation();
  
  // Add ref for the editor container to calculate relative positions
  const editorContainerRef = useRef<HTMLDivElement>(null);
  
  // Add flag to prevent remote updates from triggering socket events
  const [isApplyingRemoteUpdate, setIsApplyingRemoteUpdate] = useState(false);

  // Removed isAutoSaving state since we're using state-based approach
  
  const { updateAgentData } = getAgentStateActions();
  
  // Removed useEffect for onAutoSaveStateChange since we're not using props
  
  // Detect browser selection color on mount
  useEffect(() => {
    const detectedColor = getBrowserSelectionColor();
    setBrowserSelectionColor(detectedColor);
    console.log('Detected browser selection color:', detectedColor);
  }, []);

  // Get reactive step data to check prepare report status
  const stepData = useAgentStepData(id || '');
  
  // Get policy content and sources from agent state
  useEffect(() => {
    if (id) {
      const agentData = getAgentData(id);
      
      // Check if prepare report step is complete to disable editing using reactive step data
      const prepareReportStep = stepData?.find(
        (step) => step.type === AgentSessionStepType.PREPARE_REPORT
      );
      const isReportComplete = prepareReportStep?.status === AgentSessionStatus.COMPLETE;
      
      // Determine if the editor should be read-only based on report status or if another user is refining
      const isRefining = (agentData?.mainData as any)?.isRefining || false;
      setIsReadOnly(isReportComplete || isRefining || isRefine);
      
      // Check if this is a policy management agent and has policy content
      if (agentData?.agentType === 'policy_management' && (agentData.mainData as any)?.policyContent) {
        // Use the dynamic policy content from the session (markdown format)
        const policyContent = (agentData.mainData as any).policyContent;
        const html = convertMarkdownToHTML(policyContent);
        setHtmlContent(html);
        
        // Initialize the last processed content reference
        setLastProcessedContent(policyContent);
       
        
        // Get sources from the policy data if available
        if ((agentData.mainData as any)?.sources) {
          setSources((agentData.mainData as any).sources);
          
        }
      } 
    }
  }, [id, stepData, isRefine]);

  // Auto-save function with race condition protection
  const autoSaveContent = useCallback(async (markdownContent: string) => {
    if (!id) return;
    
    // CRITICAL: Don't autosave during remote updates to prevent race conditions
    if (isApplyingRemoteUpdate) {
      console.log('EDITOR - Skipping autosave during remote update to prevent race condition');
      return;
    }
    
    try {
      // The content is now already in Markdown format
      
      // Update agent state with markdown content
      const agentData = getAgentData(id);
      
      if (agentData?.agentType === AGENT_TYPES.POLICY_MANAGEMENT) {
        // Check if this might be a remote update by examining recent timestamp
        const lastUpdate = agentData.mainData.lastUpdate;
        if (lastUpdate?.isRemoteUpdate && Date.now() - lastUpdate.timestamp < 2000) {
          console.log('EDITOR - Skipping autosave for recent remote update');
          // Still clear lastEditedField to stop animation
          updateAgentData(id, {
            ...agentData,
            mainData: {
              ...agentData.mainData,
              lastEditedField: undefined,
            }
          });
          return;
        }

        console.log('EDITOR - Processing local autosave');
        
        // Update content first (but don't set lastEditedField here - it's already set in onUpdate)
        updateAgentData(id, {
          ...agentData,
          mainData: {
            ...agentData.mainData,
            policyContent: markdownContent, // Store as markdown
            isEdited: true,
          }
        });
        
        // Trigger autosave
        console.log('EDITOR - Calling handleAgentAutoSave');
        await handleAgentAutoSave(
          id,
          AGENT_TYPES.POLICY_MANAGEMENT,
          agentData?.agentType as unknown as AgentSubTypes
        );
        console.log('EDITOR - handleAgentAutoSave completed successfully');

        // Clear the lastEditedField after successful autosave to stop the animation
        const currentAgentData = getAgentData(id);
        if (currentAgentData) {
          console.log('EDITOR - Clearing lastEditedField after successful autosave');
          updateAgentData(id, {
            ...currentAgentData,
            mainData: {
              ...currentAgentData.mainData,
              lastEditedField: undefined, // Clear this to stop the saving animation
            }
          });
        }
      }
    } catch (error) {
      console.error('Auto-save failed:', error);
      
      // CRITICAL: Always clear lastEditedField even on error to stop the animation
      const agentData = getAgentData(id);    
      if (agentData?.agentType === AGENT_TYPES.POLICY_MANAGEMENT) {
        updateAgentData(id, {
          ...agentData,
          mainData: {
            ...agentData.mainData,
            lastEditedField: undefined, // Clear on error too
          }
        });
      }
      
      addNotification({
        type: 'error',
        title: 'Auto-save failed',
        message: 'Failed to save your changes. Please try again.',
      });
    }
  }, [id, updateAgentData, isApplyingRemoteUpdate]);

  // Debounced auto-save
  const debouncedAutoSave = useMemo(
    () => debounce(autoSaveContent, 1000, { trailing: true }),
    [autoSaveContent]
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: '' , // Start with empty content, it will be populated by useEffect
    editable: !isReadOnly,
    onUpdate: ({ editor }) => {
      // Use ref for synchronous check to prevent race condition on modal close
      if (isAwaitingRefineRef.current) {
        console.log('EDITOR - Ignoring onUpdate while awaiting refine response (ref check)');
        return;
      }

      const agentData = getAgentData(id || '');

      // Add a global check for any ongoing refine operation
      if (agentData?.agentType === 'policy_management' && (agentData.mainData as any)?.isRefining) {
        console.log('EDITOR - Ignoring onUpdate while a global refine is in progress');
        return;
      }
      
      // CRITICAL: Prevent remote updates from triggering socket events
      if (isApplyingRemoteUpdate) {
        console.log('EDITOR - Ignoring onUpdate during remote update application');
        return;
      }

      console.log('EDITOR - Processing local user edit');
      
      // Content changed, set saving state immediately
      if (agentData?.agentType === AGENT_TYPES.POLICY_MANAGEMENT) {
        updateAgentData(id || '', {
          ...agentData,
          mainData: {
            ...agentData.mainData,
            lastEditedField: 'response', // Set this immediately to start animation
          }
        });

        // Safety timeout to clear lastEditedField in case autosave gets stuck
        setTimeout(() => {
          const currentAgentData = getAgentData(id || '');
          if (currentAgentData?.mainData.lastEditedField === 'response') {
            console.log('EDITOR - Safety timeout: clearing lastEditedField');
            updateAgentData(id || '', {
              ...currentAgentData,
              mainData: {
                ...currentAgentData.mainData,
                lastEditedField: undefined,
              }
            });
          }
        }, 1000); // 1 seconds safety timeout
      }
      
      // Then trigger auto-save
      const content = editor.getHTML();
      const markdownContent = convertHTMLToMarkdown(content);
      debouncedAutoSave(markdownContent);

      // Hide the typography menu if it's open
      if (isTypographyMenuOpen) {
        setIsTypographyMenuOpen(false);
      }
    }
  });

  // This effect handles both the initial load and any subsequent remote updates
  // that are NOT animated. For animated updates, startUpdateAnimation is called directly.
  useEffect(() => {
    if (editor && htmlContent && !editor.isDestroyed) {
      // We only want this effect to run for non-animated updates.
      // If an animation is in progress, it will handle setting the content.
      if (isAnimatingUpdate) {
        return;
      }
      
      // Mark as remote update to prevent triggering onUpdate
      setIsApplyingRemoteUpdate(true);
      editor.commands.setContent(htmlContent);
      // Small delay to ensure the update is processed and then reset the flag
      setTimeout(() => setIsApplyingRemoteUpdate(false), 50);
    }
  }, [editor, htmlContent]); // isAnimatingUpdate is intentionally omitted

  // Update editor editable state when readonly changes
  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      const shouldBeEditable = !isReadOnly;
      editor.setEditable(shouldBeEditable);
    }
  }, [editor, isReadOnly]);

  // Force editor state sync when refine states change
  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      // Force a sync based on current refine states
      const currentAgentData = getAgentData(id || '');
      const currentIsRefining = currentAgentData?.agentType === AGENT_TYPES.POLICY_MANAGEMENT 
        ? (currentAgentData.mainData as any)?.isRefining || false
        : false;
      
      // Check if prepare report step is complete to disable editing
      const prepareReportStep = stepData?.find(
        (step) => step.type === AgentSessionStepType.PREPARE_REPORT
      );
      const isReportComplete = prepareReportStep?.status === AgentSessionStatus.COMPLETE;
      
      const shouldBeEditable = !currentIsRefining && !isRefine && !isAwaitingRefine && !isReportComplete;
      editor.setEditable(shouldBeEditable);
    }
  }, [editor, isRefine, isAwaitingRefine, id, stepData]);

  // Poll for remote state changes to ensure editor stays in sync
  useEffect(() => {
    if (!editor || !id) return;

    const syncEditorState = () => {
      if (editor && !editor.isDestroyed) {
        const currentAgentData = getAgentData(id);
        const currentIsRefining = currentAgentData?.agentType === AGENT_TYPES.POLICY_MANAGEMENT 
          ? (currentAgentData.mainData as any)?.isRefining || false
          : false;
        
        // Check if prepare report step is complete to disable editing
        const prepareReportStep = stepData?.find(
          (step) => step.type === AgentSessionStepType.PREPARE_REPORT
        );
        const isReportComplete = prepareReportStep?.status === AgentSessionStatus.COMPLETE;
        
        const shouldBeEditable = !currentIsRefining && !isRefine && !isAwaitingRefine && !isReportComplete;
        const currentEditable = editor.isEditable;
        
        if (currentEditable !== shouldBeEditable) {
          console.log('Editor state mismatch detected - fixing:', { currentEditable, shouldBeEditable });
          // Wrap with flag to prevent this from triggering onUpdate
          setIsApplyingRemoteUpdate(true);
          editor.setEditable(shouldBeEditable);
          setTimeout(() => setIsApplyingRemoteUpdate(false), 50);
        }
      }
    };

    // Check every 500ms for state mismatches
    const interval = setInterval(syncEditorState, 500);

    return () => clearInterval(interval);
  }, [editor, id, isRefine, isAwaitingRefine, stepData]);

  // Animation function for remote updates
  const startUpdateAnimation = useCallback(async (newMarkdown: string, newSources?: ReviewSourceTypes[]) => {
    console.log('startUpdateAnimation called', { hasEditor: !!editor, isAnimatingUpdate });
    
    if (!editor || isAnimatingUpdate) {
      console.log('Animation blocked:', { hasEditor: !!editor, isAnimatingUpdate });
      return;
    }
    
    console.log('Starting animation...');
    setIsAnimatingUpdate(true);
    
    // CRITICAL: Mark as remote update during entire animation to prevent socket events
    setIsApplyingRemoteUpdate(true);
    
    // Clear the selection overlay BEFORE the content updates to prevent jumping
    setSelectionOverlay(null);
    setPreservedSelection(null);
    setIsRefineSubmitted(false);

    if (editor) {
      editor.setEditable(false);
    }
    
    // --- New animation logic with Framer Motion ---
    await animationControls.start({
      opacity: 0,
      y: -10, // move up slightly on fade out
      transition: { duration: 0.4, ease: "easeInOut" }
    });

    // Update content when invisible
    const finalHtml = convertMarkdownToHTML(newMarkdown);
    if (editor && !editor.isDestroyed) {
      // Directly set content in the editor without triggering the useEffect
      editor.commands.setContent(finalHtml, false); // `false` prevents triggering onUpdate here
    }
    // Update state to match, but this won't trigger the useEffect because of the isAnimatingUpdate flag
    setHtmlContent(finalHtml); 
    
    if (newSources) {
      setSources(newSources);
    }
    
    // Fade in with new content
    await animationControls.start({
      opacity: 1,
      y: 0, // return to original position
      transition: { duration: 0.4, ease: "easeInOut" }
    });
    // --- End of new animation logic ---
    
    // CRITICAL: isApplyingRemoteUpdate is now cleared in the timeout below to prevent race conditions
    setIsAnimatingUpdate(false);
    setAnimationLines([]);
    setCurrentLineIndex(0);
    
    // Re-enable editor based on current state AFTER all state updates are complete
    if (editor && !editor.isDestroyed) {
      // Force a small delay to ensure state updates have propagated
      setTimeout(() => {
         if (editor && !editor.isDestroyed) {
           const currentAgentData = getAgentData(id || '');
           // Only check isRefining for policy management agents
           const currentIsRefining = currentAgentData?.agentType === AGENT_TYPES.POLICY_MANAGEMENT 
             ? (currentAgentData.mainData as any)?.isRefining || false
             : false;
           const shouldBeEditable = !currentIsRefining && !isRefine;
           editor.setEditable(shouldBeEditable);
           
           // CRITICAL: Clear remote update flag AFTER all changes are applied
           setIsApplyingRemoteUpdate(false);
         } else {
           // Handle case where editor is destroyed during timeout
           setIsApplyingRemoteUpdate(false);
         }
      }, 50);
    } else {
      // Handle case where editor doesn't exist to prevent flag getting stuck
      setIsApplyingRemoteUpdate(false);
    }
  }, [editor, isAnimatingUpdate, isReadOnly, animationControls]);

  // Listen for remote updates to agent data (placed after editor and animation function are defined)
  useEffect(() => {
    if (!id) return;

    const checkForUpdates = () => {
      const agentData = getAgentData(id);
      
      if (agentData?.agentType === 'policy_management') {
        const mainData = agentData.mainData as any;
        
        // Sync local awaiting flag with remote refining state
        const isRefining = mainData.isRefining || false;
        if (isAwaitingRefine && !isRefining) {
          isAwaitingRefineRef.current = false; // Reset ref when operation is complete
          setIsAwaitingRefine(false);
          // Don't set isReadOnly here - let the logic below handle it
        }
        
        // Determine read-only state: only true if actively refining OR if isRefine prop is true AND we're not in a completed refine state
        const shouldBeReadOnly = isRefining || (isRefine && isAwaitingRefine);
        setIsReadOnly(shouldBeReadOnly);
        
                 // Check if this is a remote update by looking at the lastUpdate timestamp
         if (mainData?.lastUpdate?.isRemoteUpdate && mainData?.policyContent) {
           console.log('EDITOR - Detected remote update, updating content');
           
           // Compare against the last processed content instead of current htmlContent
           // This prevents false triggers when switching tabs
           const newContent = mainData.policyContent;
           
           console.log('Content comparison:', {
             newContent: newContent.substring(0, 100) + '...',
             lastProcessed: lastProcessedContent.substring(0, 100) + '...',
             areEqual: newContent === lastProcessedContent,
             newLength: newContent.length,
             lastLength: lastProcessedContent.length,
             isAnimating: isAnimatingUpdate,
             isAiGenerated: mainData.lastUpdate.isAiGenerated
           });
           
           // Only update if the markdown content has actually changed and not currently animating
           if (newContent !== lastProcessedContent && !isAnimatingUpdate) {
             // Only animate if this is an AI-generated update, otherwise just update directly
             if (mainData.lastUpdate.isAiGenerated) {
               console.log('Starting animation - AI-generated content change');
               try {
                 // Pass the original markdown content instead of HTML for better line-by-line animation
                 startUpdateAnimation(mainData.policyContent, mainData.sources);
                 // Update the last processed content reference
                 setLastProcessedContent(newContent);
               } catch (error) {
                 console.error('Animation error:', error);
                 // Fallback to direct update if animation fails
                 const html = convertMarkdownToHTML(mainData.policyContent);
                 setHtmlContent(html);
                 if (mainData.sources) {
                   setSources(mainData.sources);
                 }
                 setLastProcessedContent(newContent);
               }
             } else {
               console.log('Direct update - regular collaborative edit (no animation)');
               // Direct update without animation for regular collaborative edits
               const html = convertMarkdownToHTML(mainData.policyContent);
               setHtmlContent(html);
               if (mainData.sources) {
                 setSources(mainData.sources);
               }
               setLastProcessedContent(newContent);
             }
           } else {
             console.log('Skipping update - content is the same or already animating');
           }

          // Clear the remote update flag to prevent continuous updates
          updateAgentData(id, {
            ...agentData,
            mainData: {
              ...mainData,
              lastUpdate: {
                ...mainData.lastUpdate,
                isRemoteUpdate: false,
              }
            }
          });
        }
      }
    };

    // Set up a polling mechanism to check for updates
    const interval = setInterval(checkForUpdates, 100);

    return () => {
      clearInterval(interval);
    };
  }, [id, htmlContent, isAnimatingUpdate, startUpdateAnimation, isAwaitingRefine]);



  // Get the actual browser selection color
  const getBrowserSelectionColor = () => {
    // Create a temporary element to detect the browser's selection color
    const testEl = document.createElement('div');
    testEl.style.position = 'absolute';
    testEl.style.left = '-9999px';
    testEl.style.top = '-9999px';
    testEl.style.opacity = '0';
    testEl.innerHTML = 'test text for selection';
    testEl.style.userSelect = 'text';
    (testEl.style as any).webkitUserSelect = 'text';
    (testEl.style as any).mozUserSelect = 'text';
    (testEl.style as any).msUserSelect = 'text';
    document.body.appendChild(testEl);
    
    let selectionColor = 'rgba(0, 123, 255, 0.25)'; // Default fallback
    
    try {
      // Select the text to get the actual selection color
      const range = document.createRange();
      range.selectNodeContents(testEl);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      
      // Try to get computed style of selection
      const computedStyle = window.getComputedStyle(testEl, '::selection');
      let detectedColor = computedStyle.backgroundColor;
      
      // Also try getting it from a selected range
      if (!detectedColor || detectedColor === 'transparent' || detectedColor === 'rgba(0, 0, 0, 0)') {
        // Create a span and check its selection color
        const span = document.createElement('span');
        span.textContent = 'test';
        span.style.userSelect = 'text';
        testEl.appendChild(span);
        
        const spanRange = document.createRange();
        spanRange.selectNodeContents(span);
        selection?.removeAllRanges();
        selection?.addRange(spanRange);
        
        const spanStyle = window.getComputedStyle(span, '::selection');
        detectedColor = spanStyle.backgroundColor;
      }
      
      // Validate and use the detected color
      if (detectedColor && detectedColor !== 'transparent' && detectedColor !== 'rgba(0, 0, 0, 0)') {
        selectionColor = detectedColor;
      } else {
        // Use browser-specific defaults as fallback
        const userAgent = navigator.userAgent.toLowerCase();
        if (userAgent.includes('chrome') || userAgent.includes('edge')) {
          selectionColor = 'rgba(0, 123, 255, 0.25)'; // Chrome/Edge
        } else if (userAgent.includes('firefox')) {
          selectionColor = 'rgba(0, 96, 223, 0.25)'; // Firefox
        } else if (userAgent.includes('safari')) {
          selectionColor = 'rgba(0, 122, 255, 0.25)'; // Safari
        }
      }
      
      // Clean up
      selection?.removeAllRanges();
    } catch (error) {
      console.log('Error detecting selection color, using fallback:', error);
    } finally {
      document.body.removeChild(testEl);
    }
    
    return selectionColor;
  };

  // Create visual selection overlay with proper relative positioning
  const createSelectionOverlay = (from: number, to: number, text: string) => {
    if (!editor || !editorContainerRef.current) return;

    const { view } = editor;
    const editorContainer = editorContainerRef.current;
    
    // Get all the DOM rectangles for the selected text with pixel precision
    const rects: DOMRect[] = [];
    
    try {
      // Get the bounding rect of the editor container for coordinate transformation
      const containerRect = editorContainer.getBoundingClientRect();
      
      // Create a temporary range to get the exact client rectangles
      const range = document.createRange();
      const startPos = view.domAtPos(from);
      const endPos = view.domAtPos(to);
      
      if (startPos.node && endPos.node) {
        // Handle text nodes specifically for better precision
        if (startPos.node.nodeType === Node.TEXT_NODE && endPos.node.nodeType === Node.TEXT_NODE) {
          range.setStart(startPos.node, startPos.offset);
          range.setEnd(endPos.node, endPos.offset);
        } else {
          // Fallback for element nodes
          range.setStart(startPos.node, startPos.offset);
          range.setEnd(endPos.node, endPos.offset);
        }
        
        const clientRects = range.getClientRects();
        
        // Convert to DOMRect with coordinates relative to the editor container
        for (let i = 0; i < clientRects.length; i++) {
          const rect = clientRects[i];
          // Only include rects with proper dimensions
          if (rect.width > 0 && rect.height > 0) {
            // Transform viewport coordinates to container-relative coordinates
            const relativeLeft = rect.left - containerRect.left;
            const relativeTop = rect.top - containerRect.top;
            
            rects.push(new DOMRect(
              Math.round(relativeLeft), 
              Math.round(relativeTop), 
              Math.round(rect.width), 
              Math.round(rect.height)
            ));
          }
        }
        
        range.detach(); // Clean up
      }
    } catch (error) {
      console.log('Error creating selection overlay:', error);
      return;
    }

    if (rects.length > 0) {
      console.log('Creating selection overlay with color:', browserSelectionColor);
      setSelectionOverlay({ rects, text, color: browserSelectionColor });
    }
  };

  const handleAskAI = () => {
    if (!editor || !editorContainerRef.current) return;

    const { from, to, empty } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to);

    console.log('handleAskAI called with selection:', { from, to, empty, hasText: !!selectedText.trim() });

    if (!empty && selectedText.trim()) {
      const { view } = editor;
      const editorContainer = editorContainerRef.current;
      const start = view.coordsAtPos(from);

      // Store the selection for visual preservation hack
      setPreservedSelection({ from, to, text: selectedText });

      // Ensure we have the latest browser selection color
      const currentSelectionColor = getBrowserSelectionColor();
      setBrowserSelectionColor(currentSelectionColor);
      
      // Create visual selection overlay
      createSelectionOverlay(from, to, selectedText);

      // Get the scrollable container to calculate proper relative positioning
      const scrollContainer = editorContainer.closest('.flex-grow.overflow-auto') as HTMLElement;
      if (scrollContainer) {
        const scrollContainerRect = scrollContainer.getBoundingClientRect();
        const editorContainerRect = editorContainer.getBoundingClientRect();
        
        // Calculate position relative to the editor container but accounting for scroll
        const relativeTop = start.top - editorContainerRect.top;
        const relativeLeft = start.left - editorContainerRect.left;



        setRefineModalPosition({ top: relativeTop, left: relativeLeft });
      } else {
        // Fallback if scroll container not found
        const containerRect = editorContainer.getBoundingClientRect();
        const relativeTop = start.top - containerRect.top;
        const relativeLeft = start.left - containerRect.left;
        setRefineModalPosition({ top: relativeTop, left: relativeLeft });
      }

      setSelectedTextForRefine(selectedText);
      setSelectedTextIndices({ start: from, end: to });
      setIsRefineModalOpen(true);

    } else {
      console.log('No text selected, opening floating Ask AI');
      setIsFloatingAskAiOpen(true);
    }
  };

  const handleSourceClick = (selectedSource: ReviewSourceTypes) => {
    setSelectedSource(selectedSource);
  };

  const handleTypographyClick = () => {
    setIsTypographyMenuOpen(!isTypographyMenuOpen);
  };

  // Function to handle refine text with socket call
  const handleRefineRequest = async (instructionsForChange: string) => {
    if (!id || !selectedTextForRefine || !selectedTextIndices) {
      console.error('Missing required data for refine operation');
      return;
    }

    // Set awaiting flag FIRST to prevent race condition with onUpdate
    // Use both ref (for sync check) and state (for re-render)
    isAwaitingRefineRef.current = true;
    setIsAwaitingRefine(true);

    // Disable the editor immediately upon request
    if (editor) {
      // Wrap with flag to prevent this from triggering onUpdate
      setIsApplyingRemoteUpdate(true);
      editor.setEditable(false);
      // Reset flag after a short delay
      setTimeout(() => setIsApplyingRemoteUpdate(false), 50);
    }

    try {
      const agentData = getAgentData(id);
      if (!agentData) {
        throw new Error('Agent data not found');
      }

      // Get the current response quality
      const selectedResponse = userStateSelector.getState().aiResponseType === AiResponseType.NONE
        ? AiResponseType.LITE
        : userStateSelector.getState().aiResponseType;

      const { response_mode, response_quality } = getResponseTypes(selectedResponse);

      // Find the edit response step using reactive step data
      const editStepData = stepData?.find(
        (step) => step.type === AgentSessionStepType.EDIT_RESPONSE
      );

      if (!editStepData) {
        throw new Error('Edit response step not found');
      }

      // Construct the payload
      const refinePayload = {
        agent_session_id: id,
        agent_session_step_id: editStepData.id,
        rag_input: {
          doc_type: "json" as const,
          response_quality,
          instructions_for_change: instructionsForChange,
          selected_text_to_refine: selectedTextForRefine,
          selected_text_start_index: selectedTextIndices.start,
          selected_text_end_index: selectedTextIndices.end,
          control_id: (agentData.mainData as any).controlId,
        },
        response_mode,
        task: "refine_policy_management" as const,
      };

      console.log('Sending refine request:', refinePayload);
      
      // Mark as submitted but keep selection visible
      setIsRefineSubmitted(true);
      
      // Emit the refine request
      await emitRagCreate(refinePayload as any, (response: any) => {
        handleSocketResponse(response);
      });

      // Close only the modal but keep the selection overlay visible
      setIsRefineModalOpen(false);
      console.log('Refine request sent successfully, modal closed but selection overlay preserved');

    } catch (error) {
      console.error('Error sending refine request:', error);
      addNotification({
        type: 'error',
        title: 'Refine failed',
        message: 'Failed to send refinement request. Please try again.',
      });
      
      // Re-enable the editor on failure, respecting the current state
      if (editor) {
        // Get current state to ensure we're using the latest values
        const currentAgentData = getAgentData(id || '');
        const currentIsRefining = currentAgentData?.agentType === AGENT_TYPES.POLICY_MANAGEMENT 
          ? (currentAgentData.mainData as any)?.isRefining || false
          : false;
        const shouldBeEditable = !currentIsRefining && !isRefine;
        editor.setEditable(shouldBeEditable);
      }
      isAwaitingRefineRef.current = false;
      setIsAwaitingRefine(false);
    }
  };

  const handleRefineText = (refinedText: string) => {
    editor?.chain().focus().insertContent(refinedText).run();
    handleCloseRefineModal();
  };

  const handleCloseRefineModal = () => {
    // Do not close or clear anything if a refine request was just submitted.
    // The selection overlay should persist until the update comes back.
    if (isRefineSubmitted) {
      console.log('Refine submitted, modal is already closed, not clearing selection.');
      return;
    }

    console.log('Closing refine modal and clearing selection overlay');
    setIsRefineModalOpen(false);
    setSelectedTextForRefine('');
    setSelectedTextIndices(undefined);
    setRefineModalPosition(undefined);
    setPreservedSelection(null);
    setSelectionOverlay(null);
    setIsRefineSubmitted(false);
  };

  const handleHeadingChange = (level: number | null) => {
    if (!editor) return;
    
    if (level === null) {
      // Convert to paragraph/body text
      editor.chain().focus().setParagraph().run();
    } else {
      // Convert to heading
      editor.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 }).run();
    }
    
    setIsTypographyMenuOpen(false);
  };

  const getCurrentTextType = () => {
    if (!editor) return 'Body';
    
    if (editor.isActive('heading', { level: 1 })) return 'Heading 1';
    if (editor.isActive('heading', { level: 2 })) return 'Heading 2';
    if (editor.isActive('heading', { level: 3 })) return 'Heading 3';
    return 'Body';
  };

  // Source preview rendering logic
  const renderSource = useCallback(async () => {
    if (!selectedSource) {
      return null;
    }

    const {
      source_file_name,
      source_text,
      file_name,
      file_url,
      original_file_name,
    } = selectedSource;

    // Determine the filename to use for extension extraction
    const effectiveFileName = file_name || source_file_name || original_file_name;

    // Get the source file URL, potentially refreshing it
    let sourceFileUrl = file_url;
    if (!sourceFileUrl) {
      const agentData = getAgentData(id || '');
      const sourceFileDetails = agentData?.mainData.sourceFilesUrls?.get(effectiveFileName || "");
      if (sourceFileDetails?.url) {
        sourceFileUrl = sourceFileDetails.url;
      } else {
        console.error("Source file URL not found for:", effectiveFileName);
        return null; // Cannot proceed without a URL
      }
    }

    // Refresh URL if necessary
    if (sourceFileUrl) {
      sourceFileUrl = await checkAndRefreshUrl(sourceFileUrl, effectiveFileName || '');
    }

    // Get the file extension
    const fileExtension = getFileExtension(effectiveFileName);

    const handleDownload = () => {
      if (!sourceFileUrl) return;
      const link = document.createElement("a");
      link.href = sourceFileUrl as string;
      link.download = effectiveFileName || "download";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSelectedSource(undefined);
      setListOfSourcesForSelectedSources(undefined);
    };

    // Check if the extension is supported for preview
    if (!fileExtension || !sourcePreviewSupportedFileExtensions.includes(fileExtension)) {
      console.warn(`File extension ".${fileExtension}" not supported for preview, attempting download.`);
      handleDownload();
      return null;
    }

    // Render based on file extension
    switch (fileExtension) {
      case "pdf":
        return (
          <Suspense fallback={<Spinner label="Loading PDF..." />}>
            <PDFViewer
              fileUrl={sourceFileUrl}
              isOpen={true}
              pageNumber={
                (selectedSource as PDFReviewSource).source_page_number ||
                (selectedSource as PDFReviewSource).page_number
              }
              listOfSourcesForSelectedSources={listOfSourcesForSelectedSources}
              selectedSource={selectedSource}
              setSelectedSource={setSelectedSource}
              onClose={() => {
                setSelectedSource(undefined);
                setListOfSourcesForSelectedSources(undefined);
              }}
              headerTitle={
                original_file_name || source_file_name || (file_name ?? "")
              }
              highlight={
                ((selectedSource as PDFReviewSource).source_highlight_area ||
                  (selectedSource as PDFReviewSource).highlight_area) ??
                []
              }
            />
          </Suspense>
        );
      case "jpeg":
      case "jpg":
      case "png":
      case "webp":
      case "svg":
      case "gif":
        return (
          <Suspense fallback={<Spinner label="Loading image..." />}>
            <ImageViewer
              fileUrl={sourceFileUrl}
              isOpen={true}
              onClose={() => { 
                setSelectedSource(undefined); 
                setListOfSourcesForSelectedSources(undefined);
              }}
              headerTitle={original_file_name || source_file_name || (file_name ?? "")}
              listOfSourcesForSelectedSources={listOfSourcesForSelectedSources}
              selectedSource={selectedSource}
              setSelectedSource={setSelectedSource}
            />
          </Suspense>
        );
      case "json":
        return (
          <Suspense fallback={<Spinner label="Loading JSON..." />}>
            <JSONPreviewHighlight
              open={true}
              fileUrl={sourceFileUrl}
              textToHighlight={source_text ?? ""}
              fileName={original_file_name || source_file_name || (file_name ?? "")}
              onClose={() => {
                setSelectedSource(undefined);
                setListOfSourcesForSelectedSources(undefined);
              }}
              listOfSourcesForSelectedSources={listOfSourcesForSelectedSources}
              selectedSource={selectedSource}
              setSelectedSource={setSelectedSource}
            />
          </Suspense>
        );
      case "csv":
      case "xls":
      case "xlsx":
      case "xlsm": {
        const selectedExcelSource = selectedSource as ExcelReviewSource;
        const hasSheet = !!selectedExcelSource.sheet_name;
        const isCSV = fileExtension === "csv";

        if (!hasSheet && !isCSV) {
          addNotification({
            message: "Error loading the file: Missing sheet name for Excel file.",
            type: "error",
          });
          setSelectedSource(undefined);
          setListOfSourcesForSelectedSources(undefined);
          return null;
        }
        return (
          <Suspense fallback={<Spinner label="Loading spreadsheet..." />}>
            <ExcelPreview
              fileUrl={sourceFileUrl}
              sheetName={selectedExcelSource.sheet_name ?? ""}
              open={true}
              onClose={() => {
                setSelectedSource(undefined);
                setListOfSourcesForSelectedSources(undefined);
              }}
              highlightRow={
                selectedExcelSource?.source_row_number ||
                selectedExcelSource?.row_number
              }
              listOfSourcesForSelectedSources={listOfSourcesForSelectedSources}
              selectedSource={selectedSource}
              setSelectedSource={setSelectedSource}
            />
          </Suspense>
        );
      }
      default:
        // Fallback, though the check above should catch unsupported types
        console.error(`Unhandled supported extension: ${fileExtension}`);
        handleDownload();
        return null;
    }
  }, [id, selectedSource, listOfSourcesForSelectedSources]);

  const { value: renderedSourceJsx } = useMemoAsync(
    async () => await renderSource(),
    [renderSource]
  );

  // Force bubble menu to hide when refine modal opens
  useEffect(() => {
    if (isRefineModalOpen && editor) {
      // Find and hide any visible tippy instances (bubble menu)
      const tippyInstances = document.querySelectorAll('[data-tippy-root]');
      tippyInstances.forEach((instance: any) => {
        if (instance._tippy) {
          instance._tippy.hide();
        }
      });
    }
  }, [isRefineModalOpen, editor]);

  // Handle clicking outside typography dropdown, refine modal, and selection overlay
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Handle typography dropdown
      if (!target.closest('.typography-dropdown') && !target.closest('[data-typography-trigger]')) {
        setIsTypographyMenuOpen(false);
      }
      
      // Handle refine modal
      if (!target.closest('.refine-modal-container') && isRefineModalOpen) {
        handleCloseRefineModal();
      }
      
              // Handle selection overlay - clear it when clicking outside if refine was submitted
        // BUT NOT when we're awaiting a refine response or when isRefine is true (to preserve selection during processing)
        if (isRefineSubmitted && selectionOverlay && !target.closest('.selection-overlay-dynamic') && !isAwaitingRefine && !isRefine) {
          console.log('Clicked outside selection overlay, clearing it');
          setSelectionOverlay(null);
          setPreservedSelection(null);
          setIsRefineSubmitted(false);
        }
    };

    if (isTypographyMenuOpen || isRefineModalOpen || (isRefineSubmitted && selectionOverlay)) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
      }, [isTypographyMenuOpen, isRefineModalOpen, isRefineSubmitted, selectionOverlay, isAwaitingRefine, isRefine]);

  // Update selection overlay on window resize/scroll
  useEffect(() => {
    if (!selectionOverlay || !preservedSelection || !editor || !editorContainerRef.current) return;

    const updateOverlay = () => {
      const { from, to, text } = preservedSelection;
      createSelectionOverlay(from, to, text);
    };

    const handleResize = () => updateOverlay();
    const handleScroll = () => updateOverlay();

    // Listen for scroll events on the editor container instead of window
    const editorContainer = editorContainerRef.current.closest('.flex-grow.overflow-auto') as HTMLElement;
    
    window.addEventListener('resize', handleResize);
    if (editorContainer) {
      editorContainer.addEventListener('scroll', handleScroll);
    } else {
      // Fallback to window scroll if container not found
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (editorContainer) {
        editorContainer.removeEventListener('scroll', handleScroll);
      } else {
        window.removeEventListener('scroll', handleScroll, true);
      }
    };
  }, [selectionOverlay, preservedSelection, editor, editorContainerRef]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedAutoSave.cancel();
    };
  }, [debouncedAutoSave]);

  return (
    <>
      <div className="flex-grow overflow-auto">
        <div className="px-8 py-5 max-w-[95%] mx-auto">
          {editor && (
            <BubbleMenu 
              editor={editor} 
              tippyOptions={{ 
                duration: 100,
                placement: 'top',
              }}
              shouldShow={({ editor, from, to }) => {
                // Do not show if the refine modal is open or a refine is in progress
                if (isRefineModalOpen || isAwaitingRefine || isRefine) {
                  return false;
                }

                // Get the selected text content
                const textContent = editor.state.doc.textBetween(from, to);

                // Show the menu only if there's a selection that contains actual text
                return from !== to && textContent.trim().length > 0;
              }}
            >
              <div 
                className="bubble-menu-container"
                onMouseDown={(e) => {
                  // CRITICAL: Prevent mousedown from stealing focus and losing selection
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <button
                  type="button"
                  onClick={handleAskAI}
                  className="p-1 rounded text-blue-500 flex items-center space-x-1"
                >
                  <Stars size={14} />
                  <span className="text-xs font-medium">Ask Zania</span>
                </button>
                <div className="h-4 border-r border-gray-200 mx-0.5" />
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleMark('bold').run()}
                  className={`p-1 rounded text-blue-500 ${editor.isActive('bold') ? 'bg-gray-200' : ''}`}
                >
                  <BoldIcon size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleMark('italic').run()}
                  className={`p-1 rounded text-blue-500 ${editor.isActive('italic') ? 'bg-gray-200' : ''}`}
                >
                  <ItalicIcon size={14} />
                </button>
                <div className="h-4 border-r border-gray-200 mx-0.5" />
                <div className="relative">
                  <button
                    type="button"
                    onClick={handleTypographyClick}
                    data-typography-trigger
                    className="p-1 rounded text-blue-500 flex items-center"
                  >
                    <span className="text-xs font-medium">Aa</span>
                  </button>
                  {isTypographyMenuOpen && (
                    <div className="typography-dropdown">
                      <button
                        type="button"
                        onClick={() => handleHeadingChange(1)}
                        className={`typography-option ${getCurrentTextType() === 'Heading 1' ? 'active' : ''}`}
                      >
                        <span className="text-lg font-semibold">Heading 1</span>
                        {getCurrentTextType() === 'Heading 1' && <span className="checkmark">✓</span>}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleHeadingChange(2)}
                        className={`typography-option ${getCurrentTextType() === 'Heading 2' ? 'active' : ''}`}
                      >
                        <span className="text-base font-semibold">Heading 2</span>
                        {getCurrentTextType() === 'Heading 2' && <span className="checkmark">✓</span>}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleHeadingChange(3)}
                        className={`typography-option ${getCurrentTextType() === 'Heading 3' ? 'active' : ''}`}
                      >
                        <span className="text-sm font-semibold">Heading 3</span>
                        {getCurrentTextType() === 'Heading 3' && <span className="checkmark">✓</span>}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleHeadingChange(null)}
                        className={`typography-option ${getCurrentTextType() === 'Body' ? 'active' : ''}`}
                      >
                        <span className="text-sm">Body</span>
                        {getCurrentTextType() === 'Body' && <span className="checkmark">✓</span>}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </BubbleMenu>
          )}
          
          <motion.div 
            ref={editorContainerRef}
            animate={animationControls} 
            className={`w-full relative ${isAwaitingRefine || isRefine || isReadOnly ? 'cursor-not-allowed' : ''}`}
          >
            <TipTapEditorContent 
              editor={editor} 
              className="focus:outline-none" 
            />
            
            {/* Overlay to completely disable interaction while awaiting refine or when isRefine is true */}
            {(isAwaitingRefine || isRefine) && (
              <div 
                className="absolute inset-0 bg-transparent z-10"
                style={{ cursor: 'not-allowed' }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              />
            )}

            {/* Custom Selection Overlay */}
            {selectionOverlay && selectionOverlay.rects.map((rect, index) => (
              <div
                key={index}
                className={`selection-overlay-dynamic ${isRefineSubmitted ? 'selection-overlay-dismissible' : ''}`}
                style={{
                  position: 'absolute',
                  left: `${rect.left}px`,
                  top: `${rect.top}px`,
                  width: `${rect.width}px`,
                  height: `${rect.height}px`,
                  backgroundColor: selectionOverlay.color,
                  pointerEvents: isRefineSubmitted ? 'auto' : 'none',
                  cursor: isRefineSubmitted ? 'pointer' : 'default',
                  zIndex: isRefineSubmitted ? 2 : 1,
                }}
                onClick={isRefineSubmitted ? () => {
                  console.log('Selection overlay clicked, clearing it');
                  setSelectionOverlay(null);
                  setPreservedSelection(null);
                  setIsRefineSubmitted(false);
                } : undefined}
                title={isRefineSubmitted ? 'Click to dismiss selection' : undefined}
              />
            ))}

            {/* Refine Text Modal - moved inside editor container for correct positioning */}
            <RefineTextModal
              isOpen={isRefineModalOpen}
              onClose={handleCloseRefineModal}
              selectedText={selectedTextForRefine}
              onRefine={handleRefineText}
              onRefineRequest={handleRefineRequest}
              position={refineModalPosition}
            />
          </motion.div>
          
          {/* Add inline sources at the end of the document */}
          {sources && sources.length > 0 && (
            <div className="mt-8 pt-4 border-t border-gray-200">
              <div className="relative">
                <InlineSources
                  sources={sources}
                  onSourceClick={handleSourceClick}
                  setListOfSourcesForSelectedSources={setListOfSourcesForSelectedSources}
                  tooltipPlacement="top"
                  position="static"
                />
              </div>
            </div>
          )}
          
          {isFloatingAskAiOpen && <AskAi />}
       
        </div>
        
        <style>
          {`
            .ProseMirror {
              outline: none;
              min-height: 100px;
            }
            
            .ProseMirror h1 {
              font-size: 1.75rem;
              font-weight: 600;
              margin-top: 0;
              margin-bottom: 1rem;
              color: #333;
            }
            
            .ProseMirror h2 {
              font-size: 1.375rem;
              font-weight: 600;
              margin-top: 1.5rem;
              margin-bottom: 0.75rem;
              color: #333;
            }
            
            .ProseMirror h3 {
              font-size: 1.125rem;
              font-weight: 600;
              margin-top: 1.25rem;
              margin-bottom: 0.75rem;
              color: #333;
            }
            
            .ProseMirror p {
              margin-bottom: 1rem;
              line-height: 1.6;
            }
            
            .ProseMirror ul {
              list-style-type: disc;
              margin-left: 1.5rem;
              margin-bottom: 1rem;
            }
            
            .ProseMirror ol {
              list-style-type: decimal;
              margin-left: 1.5rem;
              margin-bottom: 1rem;
            }
            
            .ProseMirror li {
              margin-bottom: 0.5rem;
            }
            
            .ProseMirror li:last-child {
              margin-bottom: 0;
            }
            
            .ProseMirror blockquote {
              border-left: 3px solid #ddd;
              padding-left: 1rem;
              color: #666;
              font-style: italic;
              margin: 1rem 0;
            }
            
            .ProseMirror a {
              color: #2563eb;
              text-decoration: underline;
              cursor: pointer;
            }
            
            /* Custom style for bubble menu */
            .tippy-box {
              border-radius: 0.5rem !important;
            }
            
            .tippy-content {
              padding: 0 !important;
            }

            /* Custom Selection Overlay - Uses dynamically detected browser color */
            .selection-overlay-dynamic {
              transition: none !important;
              animation: none !important;
              border: none !important;
              outline: none !important;
              z-index: 1 !important; /* Ensure it stays within the editor context */
            }

            /* Dismissible selection overlay styling */
            .selection-overlay-dismissible {
              transition: opacity 0.2s ease !important;
              box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.3) !important;
              z-index: 2 !important; /* Slightly higher when dismissible */
            }

            .selection-overlay-dismissible:hover {
              opacity: 0.8 !important;
              box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.5) !important;
            }

            /* Make bubble menu not interfere with selection */
            .tippy-box {
              pointer-events: auto !important;
              -webkit-user-select: none !important;
              -moz-user-select: none !important;
              -ms-user-select: none !important;
              user-select: none !important;
            }

            .bubble-menu-container {
              -webkit-user-select: none !important;
              -moz-user-select: none !important;
              -ms-user-select: none !important;
              user-select: none !important;
            }

            .bubble-menu-container button {
              -webkit-user-select: none !important;
              -moz-user-select: none !important;
              -ms-user-select: none !important;
              user-select: none !important;
            }
            
            /* Table styling */
            .ProseMirror table {
              border-collapse: collapse;
              table-layout: auto;
              width: auto;
              margin: 1rem 0;
              overflow: hidden;
            }

            .ProseMirror td,
            .ProseMirror th {
              min-width: 120px;
              border: 1px solid #ddd;
              padding: 8px 12px;
              vertical-align: top;
              box-sizing: border-box;
              position: relative;
              white-space: nowrap;
            }

            .ProseMirror th {
              font-weight: 600;
              text-align: left;
              background-color: #f8f9fa;
            }

            .ProseMirror .column-resize-handle {
              position: absolute;
              right: -2px;
              top: 0;
              bottom: -2px;
              width: 4px;
              background-color: #adf;
              pointer-events: none;
            }

            .ProseMirror table.resize-cursor {
              cursor: ew-resize;
              cursor: col-resize;
            }

            /* Additional bubble menu styling */
            .bubble-menu-container {
              display: flex;
              flex-direction: row;
              align-items: center;
              padding: 4px;
              gap: 4px;
              background: #FFFFFF;
              border: 1px solid #E4E4E7;
              box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
              border-radius: 8px;
              z-index: 3;
              width: auto;
              min-width: 192px;
            }

            /* Typography dropdown styling */
            .typography-dropdown {
              position: absolute;
              top: 100%;
              left: -160px;
              margin-top: 10px;
              background: #FFFFFF;
              border: 1px solid #E4E4E7;
              box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
              border-radius: 8px;
              z-index: 10;
              width: 192px;
              overflow: hidden;
            }

            .typography-option {
              display: flex;
              align-items: center;
              justify-content: space-between;
              width: 100%;
              padding: 8px 12px;
              text-align: left;
              background: transparent;
              border: none;
              cursor: pointer;
              transition: background-color 0.2s;
              font-size: 14px;
              color: #374151;
            }

            .typography-option:hover {
              background-color: #F3F4F6;
            }

            .typography-option.active {
              background-color: #F3F4F6;
              color: #374151;
            }

            .checkmark {
              color: #374151;
              font-weight: bold;
              font-size: 12px;
            }
          `}
        </style>
      </div>
      {renderedSourceJsx}
    </>
  );
};

export default EditorContent;
